import React, { useState, useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from './Navbar';
import { Skeleton } from '../components/ui/skeleton';
import { SimulationBanner, SimulationProvider } from './SimulationBanner';
import GuidedDemoTour from './GuidedDemoTour';
import ProductTour from './ProductTour';
import TrialExpiryBanner from './TrialExpiryBanner';
import LiveSavingsTicker from './LiveSavingsTicker';

const Layout = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [showTour, setShowTour] = useState(false);

  // Don't show the demo tour on admin pages — they're for hands-on work,
  // not first-time discovery, and the overlay sits on top of action buttons.
  const isAdminRoute = location.pathname.startsWith('/admin');

  useEffect(() => {
    if (user && user.email === 'demo@cartieai.com' && !isAdminRoute) {
      const tourSeen = sessionStorage.getItem('demo_tour_seen');
      if (!tourSeen) {
        const timer = setTimeout(() => setShowTour(true), 1500);
        return () => clearTimeout(timer);
      }
    } else if (isAdminRoute && showTour) {
      // Hide the tour if user navigates to admin while it's visible
      setShowTour(false);
    }
  }, [user, isAdminRoute]);  // eslint-disable-line react-hooks/exhaustive-deps

  const handleCloseTour = () => {
    setShowTour(false);
    sessionStorage.setItem('demo_tour_seen', 'true');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center">
        <div className="text-center space-y-4">
          <Skeleton className="h-12 w-12 rounded-full mx-auto" />
          <Skeleton className="h-4 w-32 mx-auto" />
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return (
    <SimulationProvider>
      <div className="theme-public-light min-h-screen bg-white text-slate-900">
        <SimulationBanner />
        <TrialExpiryBanner />
        {/* Demo-only positive ticker (v32) — replaces "Trial Ended" psychology
            for public demo viewers with a brand-aligned signal. Shown only
            for the demo account; real customers see their own dashboards. */}
        {user && user.email === 'demo@cartieai.com' && <LiveSavingsTicker />}
        <Navbar />
        <main className="max-w-7xl mx-auto">
          <Outlet />
        </main>
        <GuidedDemoTour isActive={showTour} onClose={handleCloseTour} />
        {/* ProductTour fires for non-demo users on first authed login —
             the demo account already has GuidedDemoTour above. */}
        {user && user.email !== 'demo@cartieai.com' && <ProductTour />}
      </div>
    </SimulationProvider>
  );
};

export default Layout;
